/**
 * Mock Fetch Handler
 * Interceptuje API volania a vracia mock dáta
 */

import mockConfig from "./config";
import { officials } from "./data/officials";
import { ovms } from "./data/ovms";
import { legitimates } from "./data/legitimates";
import { requests, requestsPublic } from "./data/requests";
import { initiatives, initiativesPublic } from "./data/initiatives";
import { statements, statementsPublic } from "./data/statements";
import { financialInstitutions } from "./data/financialInstitutions";
import { dictionaries } from "./data/dictionary";
import { statistics } from "./data/statistics";
import { internalUsers } from "./data/internalUsers";
import { admins } from "./data/admins";
import { whoLooked } from "./data/whoLooked";
import { profiles, getProfileByRole } from "./data/profile";

// Simulácia oneskorenia siete
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Parsovanie query parametrov z URL
const parseQueryParams = (url) => {
  const params = {};
  const queryString = url.split("?")[1];
  if (queryString) {
    queryString.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      params[decodeURIComponent(key)] = decodeURIComponent(value || "");
    });
  }
  return params;
};

// Extrakcia ID z URL
const extractId = (url, pattern) => {
  const match = url.match(pattern);
  return match ? match[1] : null;
};

// Filtrovanie a stránkovanie dát
const filterAndPaginate = (data, params) => {
  let filtered = [...data];

  // Aplikovanie filtrov - podpora pre filter[x].field / operation / value format
  const filterMap = {};
  Object.keys(params).forEach((key) => {
    const match = key.match(/^filter\[(\d+)\]\.(\w+)$/);
    if (match) {
      const idx = match[1];
      const prop = match[2];
      if (!filterMap[idx]) filterMap[idx] = {};
      filterMap[idx][prop] = params[key];
    }
  });

  // Aplikovanie filtrov z filter[x] formátu
  Object.values(filterMap).forEach((filter) => {
    if (filter.field && filter.value) {
      filtered = filtered.filter((item) => {
        const value = item[filter.field];
        const searchValue = filter.value.toLowerCase();

        if (value === undefined || value === null) return false;

        switch (filter.operation) {
          case "CONTAINS":
            return String(value).toLowerCase().includes(searchValue);
          case "EQ":
            return String(value).toLowerCase() === searchValue;
          case "STARTWITH":
            return String(value).toLowerCase().startsWith(searchValue);
          case "ENDWITH":
            return String(value).toLowerCase().endsWith(searchValue);
          case "GT":
            return parseFloat(value) > parseFloat(filter.value);
          case "LT":
            return parseFloat(value) < parseFloat(filter.value);
          case "GTE":
            return parseFloat(value) >= parseFloat(filter.value);
          case "LTE":
            return parseFloat(value) <= parseFloat(filter.value);
          default:
            return String(value).toLowerCase().includes(searchValue);
        }
      });
    }
  });

  // Aplikovanie jednoduchých filtrov (query params)
  Object.keys(params).forEach((key) => {
    if (
      key !== "page" &&
      key !== "pageSize" &&
      key !== "size" &&
      key !== "sort" &&
      key !== "order" &&
      key !== "q" &&
      !key.startsWith("filter[") &&
      params[key]
    ) {
      filtered = filtered.filter((item) => {
        const value = item[key];
        if (typeof value === "string") {
          return value.toLowerCase().includes(params[key].toLowerCase());
        }
        if (typeof value === "object" && value?.name) {
          return value.name.toLowerCase().includes(params[key].toLowerCase());
        }
        return String(value) === params[key];
      });
    }
  });

  // Textové vyhľadávanie (q parameter)
  if (params.q) {
    const searchTerm = params.q.toLowerCase();
    filtered = filtered.filter((item) => {
      return Object.values(item).some((value) => {
        if (typeof value === "string") {
          return value.toLowerCase().includes(searchTerm);
        }
        if (typeof value === "object" && value?.name) {
          return value.name.toLowerCase().includes(searchTerm);
        }
        return false;
      });
    });
  }

  // Sortovanie
  if (params.sort) {
    const sortField = params.sort;
    const order = params.order === "DESC" ? -1 : 1;
    filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal < bVal) return -1 * order;
      if (aVal > bVal) return 1 * order;
      return 0;
    });
  }

  const count = filtered.length;
  const page = parseInt(params.page) || 0;
  const pageSize = parseInt(params.pageSize) || parseInt(params.size) || 20;

  // Stránkovanie
  const start = page * pageSize;
  const items = filtered.slice(start, start + pageSize);

  // Vrátime formát kompatibilný s Grid komponentom
  return {
    items,
    count,
  };
};

// Mapovanie URL na mock dáta
const routeHandlers = {
  // ============ ČESKÉ API CESTY ============

  // Verejnosť - Funkcionári
  "GET /verejnost/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /verejnost/api/funkcionari/:id": (id) =>
    officials.find((o) => o.id === id),

  // Podpurný orgán (OVM) - Funkcionári
  "GET /podpurny-organ/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /podpurny-organ/api/funkcionari/:id": (id) =>
    officials.find((o) => o.id === id),
  "GET /podpurny-organ/api/funkcionari/bez-udaju": (params) =>
    filterAndPaginate(
      officials.filter((o) => !o.email),
      params,
    ),

  // Administrátor - Funkcionári
  "GET /administrator/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /administrator/api/funkcionari/:id": (id) =>
    officials.find((o) => o.id === id),
  "GET /administrator/api/funkcionari/pozdni": (params) =>
    filterAndPaginate(officials.slice(0, 3), params),
  "GET /administrator/api/funkcionari/chybejici": (params) =>
    filterAndPaginate(officials.slice(0, 2), params),
  "GET /administrator/api/funkcionari/neuplne": (params) =>
    filterAndPaginate(officials.slice(0, 1), params),

  // Správce - Funkcionári
  "GET /spravce/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /spravce/api/funkcionari/:id": (id) =>
    officials.find((o) => o.id === id),
  "GET /spravce/api/funkcionari/pozdni": (params) =>
    filterAndPaginate(
      officials.filter(
        (o) =>
          o.state?.name === "LATE" ||
          o.id.includes("003") ||
          o.id.includes("005"),
      ),
      params,
    ),
  "GET /spravce/api/funkcionari/chybejici": (params) =>
    filterAndPaginate(
      officials.filter(
        (o) =>
          o.state?.name === "MISSING" ||
          o.id.includes("002") ||
          o.id.includes("007"),
      ),
      params,
    ),
  "GET /spravce/api/funkcionari/neuplne": (params) =>
    filterAndPaginate(
      officials.filter(
        (o) => o.state?.name === "INCOMPLETE" || o.id.includes("001"),
      ),
      params,
    ),

  // Oprávnený orgán - Funkcionári
  "GET /opravneny-organ/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /opravneny-organ/api/funkcionari/:id": (id) =>
    officials.find((o) => o.id === id),

  // Soudce - Funkcionári
  "GET /soudce/api/funkcionari": (params) =>
    filterAndPaginate(officials, params),
  "GET /soudce/api/funkcionari/:id": (id) => officials.find((o) => o.id === id),

  // OVMs - Podpurné orgány
  "GET /podpurny-organ/api/podpurne-organy": (params) =>
    filterAndPaginate(ovms, params),
  "GET /spravce/api/podpurne-organy": (params) =>
    filterAndPaginate(ovms, params),
  "GET /spravce/api/podpurne-organy/bez-funkcionaru": (params) =>
    filterAndPaginate(
      ovms.filter((o) => o.officialsCount < 5),
      params,
    ),

  // Oznámení
  "GET /verejnost/api/oznameni": (params) =>
    filterAndPaginate(statementsPublic || statements, params),
  "GET /podpurny-organ/api/oznameni": (params) =>
    filterAndPaginate(statements, params),
  "GET /administrator/api/oznameni": (params) =>
    filterAndPaginate(statements, params),
  "GET /spravce/api/oznameni": (params) =>
    filterAndPaginate(statements, params),
  "GET /spravce/api/oznameni/:id": (id) => statements.find((s) => s.id === id),

  // Žádosti
  "GET /verejnost/api/zadosti": (params) =>
    filterAndPaginate(requestsPublic || requests, params),
  "GET /verejnost-prihlasen/api/zadosti": (params) =>
    filterAndPaginate(requests, params),
  "GET /podpurny-organ/api/zadosti": (params) =>
    filterAndPaginate(requests, params),
  "GET /administrator/api/zadosti": (params) =>
    filterAndPaginate(requests, params),
  "GET /spravce/api/zadosti": (params) => filterAndPaginate(requests, params),
  "GET /spravce/api/zadosti/:id": (id) => requests.find((r) => r.id === id),

  // Podnety
  "GET /verejnost/api/podnety": (params) =>
    filterAndPaginate(initiativesPublic || initiatives, params),
  "GET /verejnost-prihlasen/api/podnety": (params) =>
    filterAndPaginate(initiatives, params),
  "GET /podpurny-organ/api/podnety": (params) =>
    filterAndPaginate(initiatives, params),
  "GET /administrator/api/podnety": (params) =>
    filterAndPaginate(initiatives, params),
  "GET /spravce/api/podnety": (params) =>
    filterAndPaginate(initiatives, params),
  "GET /spravce/api/podnety/:id": (id) => initiatives.find((i) => i.id === id),

  // Archivace
  "GET /spravce/api/archivace/funkcionari": (params) =>
    filterAndPaginate(
      officials.filter((o) => !o.active || o.archived?.length > 0),
      params,
    ),
  "GET /spravce/api/archivace/zadosti": (params) =>
    filterAndPaginate(
      requests.filter(
        (r) => r.state?.name === "ACCEPT" || r.state?.name === "REJECT",
      ),
      params,
    ),
  "GET /spravce/api/archivace/podnety": (params) =>
    filterAndPaginate(
      initiatives.filter(
        (i) => i.state?.name === "REPLY" || i.state?.name === "REJECT",
      ),
      params,
    ),

  // Oprávnění (starý endpoint)
  "GET /spravce/api/opravneni": (params) =>
    filterAndPaginate(legitimates, params),
  "GET /spravce/api/opravneni/:id": (id) =>
    legitimates.find((l) => l.id === id),

  // Oprávněné orgány (nový endpoint)
  "GET /spravce/api/opravnene-organy": (params) =>
    filterAndPaginate(legitimates, params),
  "GET /spravce/api/opravnene-organy/:id": (id) =>
    legitimates.find((l) => l.id === id),
  "GET /spravce/api/opravnene-organy/prihlasovaci-udaje/generovat": () => ({
    username: `user_${Date.now()}`,
    password: `Pass${Math.random().toString(36).substring(2, 10)}!`,
  }),

  // Finanční instituce
  "GET /spravce/api/financni-instituce": (params) =>
    filterAndPaginate(financialInstitutions, params),
  "GET /spravce/api/financni-instituce/:id": (id) =>
    financialInstitutions.find((f) => f.id === id),

  // Číselníky - hlavný endpoint
  "GET /verejnost/api/ciselniky": () => dictionaries,
  "GET /podpurny-organ/api/ciselniky": () => dictionaries,
  "GET /administrator/api/ciselniky": () => dictionaries,
  "GET /spravce/api/ciselniky": () => dictionaries,

  // Číselníky - jednotlivé typy
  "GET /api/ciselniky/cenny-papiry": (params) =>
    filterAndPaginate(
      dictionaries.securityTypes.map((d) => ({ id: d.id, name: d.name })),
      params,
    ),
  "GET /api/ciselniky/nemovite-veci": (params) =>
    filterAndPaginate(
      [
        { id: "rv-001", name: "byt", publicPart: true },
        { id: "rv-002", name: "rodinný dům", publicPart: true },
        { id: "rv-003", name: "pozemek", publicPart: true },
        { id: "rv-004", name: "garáž", publicPart: false },
        { id: "rv-005", name: "chata/chalupa", publicPart: true },
        { id: "rv-006", name: "jiná nemovitost", publicPart: false },
      ],
      params,
    ),
  "GET /api/ciselniky/druhy-organizace": (params) =>
    filterAndPaginate(
      dictionaries.ovmTypes.map((d) => ({ id: d.id, name: d.name })),
      params,
    ),
  "GET /api/ciselniky/funkce": (params) =>
    filterAndPaginate(
      dictionaries.functionTypes.map((d) => ({
        id: d.id,
        name: d.name,
        organizationType: { id: "ovmt-001", name: "ministerstvo" },
      })),
      params,
    ),
  "GET /api/ciselniky/organizace": (params) =>
    filterAndPaginate(
      [
        { id: "org-001", name: "Ministerstvo financí" },
        { id: "org-002", name: "Ministerstvo vnitra" },
        { id: "org-003", name: "Poslanecká sněmovna" },
        { id: "org-004", name: "Senát ČR" },
        { id: "org-005", name: "Magistrát hl. m. Prahy" },
      ],
      params,
    ),
  "GET /api/ciselniky/typ-cinnosti": (params) =>
    filterAndPaginate(
      [
        { id: "tc-001", name: "podnikání", secretPart: false },
        { id: "tc-002", name: "vedlejší činnost", secretPart: false },
        { id: "tc-003", name: "konzultace", secretPart: true },
        { id: "tc-004", name: "poradenství", secretPart: false },
      ],
      params,
    ),
  "GET /api/ciselniky/typ-media": (params) =>
    filterAndPaginate(
      [
        { id: "tm-001", name: "televize" },
        { id: "tm-002", name: "rozhlas" },
        { id: "tm-003", name: "tisk" },
        { id: "tm-004", name: "internet" },
      ],
      params,
    ),
  "GET /api/ciselniky/typ-prijmu": (params) =>
    filterAndPaginate(
      [
        { id: "tp-001", name: "plat" },
        { id: "tp-002", name: "odměna" },
        { id: "tp-003", name: "honorář" },
        { id: "tp-004", name: "dividenda" },
        { id: "tp-005", name: "nájem" },
      ],
      params,
    ),
  "GET /api/ciselniky/vykony-podnikani": (params) =>
    filterAndPaginate(
      [
        { id: "vp-001", name: "výkon advokacie" },
        { id: "vp-002", name: "výkon notářství" },
        { id: "vp-003", name: "výkon exekutorské činnosti" },
        { id: "vp-004", name: "výkon auditorské činnosti" },
      ],
      params,
    ),
  "GET /api/ciselniky/vykony-podnikani-poslanci": (params) =>
    filterAndPaginate(
      [
        { id: "vpp-001", name: "vlastní podnikání" },
        { id: "vpp-002", name: "účast ve společnosti" },
        { id: "vpp-003", name: "členství v orgánech" },
      ],
      params,
    ),
  "GET /api/ciselniky/nabyti-movite-veci": (params) =>
    filterAndPaginate(
      [
        { id: "nmv-001", name: "koupě" },
        { id: "nmv-002", name: "dar" },
        { id: "nmv-003", name: "dědictví" },
        { id: "nmv-004", name: "výhra" },
      ],
      params,
    ),
  "GET /api/ciselniky/nabyti-nemovite-veci": (params) =>
    filterAndPaginate(
      dictionaries.acquisitionMethods.map((d) => ({ id: d.id, name: d.name })),
      params,
    ),
  "GET /api/ciselniky/zpusob-pro-medium": (params) =>
    filterAndPaginate(
      [
        { id: "zpm-001", name: "vlastnictví" },
        { id: "zpm-002", name: "spoluvlastnictví" },
        { id: "zpm-003", name: "podíl" },
      ],
      params,
    ),
  "GET /api/ciselniky/zpusob-vlastnictvi": (params) =>
    filterAndPaginate(
      dictionaries.ownershipTypes.map((d) => ({ id: d.id, name: d.name })),
      params,
    ),

  // Statistiky
  "GET /spravce/api/statistiky": () => statistics,
  "GET /administrator/api/statistiky": () => statistics,

  // Interní uživatelé
  "GET /spravce/api/interni-uzivatele": (params) =>
    filterAndPaginate(internalUsers, params),
  "GET /spravce/api/interni-uzivatele/:id": (id) =>
    internalUsers.find((u) => u.id === id),

  // Správci (systémoví administrátori)
  "GET /spravce/api/spravci": (params) => filterAndPaginate(admins, params),
  "GET /spravce/api/spravci/:id": (id) => admins.find((a) => a.id === id),

  // Funkcionar - Nahlizeni (Kdo na mě nahlížel)
  "GET /funkcionar/api/profil/nahlizeni": (params) =>
    filterAndPaginate(whoLooked, params),

  // Help API
  "GET /api/help/placements/:id": (id) => ({
    id,
    text: "Toto je mock nápověda pro testovanie.",
    title: "Nápověda",
  }),
  "GET /api/help/getall": () => ({
    PUBLIC_HOME_SEARCH: {
      placement: "PUBLIC_HOME_SEARCH",
      text: "Nápověda pro vyhledávání osob na hlavní stránce.",
    },
    HOUSE_NUMBER: {
      placement: "HOUSE_NUMBER",
      text: "Zadejte číslo popisné nebo orientační.",
    },
    ADD_REMOVE_WP: {
      placement: "ADD_REMOVE_WP",
      text: "Přidání a odebrání funkce funkcionáře.",
    },
    STATEMENT_TYPE: {
      placement: "STATEMENT_TYPE",
      text: "Druh oznámení - vstupní, průběžné, výstupní.",
    },
    REQUEST_ADRESS: {
      placement: "REQUEST_ADRESS",
      text: "Adresa pro doručování odpovědi na žádost.",
    },
    REQUEST_TEXT: {
      placement: "REQUEST_TEXT",
      text: "Text žádosti o nahlédnutí do oznámení.",
    },
    INITIATIVE_ADRESS: {
      placement: "INITIATIVE_ADRESS",
      text: "Adresa pro doručování odpovědi na podnět.",
    },
    INITIATIVE_TEXT: {
      placement: "INITIATIVE_TEXT",
      text: "Text podnětu k prošetření.",
    },
    OVM_BTN_ADD: {
      placement: "OVM_BTN_ADD",
      text: "Přidání nového funkcionáře do systému.",
    },
    OVM_BTN_IMPORT: {
      placement: "OVM_BTN_IMPORT",
      text: "Import funkcionářů ze CSV souboru.",
    },
    OVM_BTN_TEMPLATE: {
      placement: "OVM_BTN_TEMPLATE",
      text: "Stažení šablony pro import.",
    },
    OVM_BTN_GENERATE: {
      placement: "OVM_BTN_GENERATE",
      text: "Generování přihlašovacích údajů.",
    },
    IN_BIRTH_COUNRTY: {
      placement: "IN_BIRTH_COUNRTY",
      text: "Stát narození funkcionáře.",
    },
    IN_BIRTH_MUNICIPALITY: {
      placement: "IN_BIRTH_MUNICIPALITY",
      text: "Obec narození funkcionáře.",
    },
    IN_EMAIL: {
      placement: "IN_EMAIL",
      text: "E-mailová adresa pro komunikaci.",
    },
  }),

  // ============ ANGLICKÉ API CESTY (záloha) ============

  // Officials
  "GET /api/officials": (params) => filterAndPaginate(officials, params),
  "GET /api/officials/public": (params) => filterAndPaginate(officials, params),
  "GET /api/officials/:id": (id) => officials.find((o) => o.id === id),

  // OVMs
  "GET /api/ovms": (params) => filterAndPaginate(ovms, params),
  "GET /api/ovms/all": () => ovms,
  "GET /api/ovms/:id": (id) => ovms.find((o) => o.id === id),

  // Legitimates
  "GET /api/legitimates": (params) => filterAndPaginate(legitimates, params),
  "GET /api/legitimates/:id": (id) => legitimates.find((l) => l.id === id),

  // Requests
  "GET /api/requests": (params) => filterAndPaginate(requests, params),
  "GET /api/requests/public": (params) =>
    filterAndPaginate(requestsPublic, params),
  "GET /api/requests/:id": (id) => requests.find((r) => r.id === id),

  // Initiatives
  "GET /api/initiatives": (params) => filterAndPaginate(initiatives, params),
  "GET /api/initiatives/public": (params) =>
    filterAndPaginate(initiativesPublic, params),
  "GET /api/initiatives/:id": (id) => initiatives.find((i) => i.id === id),

  // Statements
  "GET /api/statements": (params) => filterAndPaginate(statements, params),
  "GET /api/statements/public": (params) =>
    filterAndPaginate(statementsPublic || statements, params),
  "GET /api/statements/:id": (id) => statements.find((s) => s.id === id),
  "GET /api/statements/official/:officialId": (officialId) =>
    statements.filter((s) => s.officialId === officialId),

  // Financial Institutions
  "GET /api/financial-institutions": (params) =>
    filterAndPaginate(financialInstitutions, params),
  "GET /api/financial-institutions/:id": (id) =>
    financialInstitutions.find((f) => f.id === id),

  // Dictionaries
  "GET /api/dictionaries": () => dictionaries,
  "GET /api/dictionaries/:type": (type) => dictionaries[type] || [],

  // Statistics
  "GET /api/statistics": () => statistics,
  "GET /api/statistics/overview": () => statistics.overview,

  // Internal Users
  "GET /api/internal-users": (params) =>
    filterAndPaginate(internalUsers, params),
  "GET /api/internal-users/:id": (id) => internalUsers.find((u) => u.id === id),

  // Profile / Auth
  "GET /api/profile": () => getProfileByRole(mockConfig.currentUserRole),
  "GET /api/auth/user": () => getProfileByRole(mockConfig.currentUserRole),

  // Archive (historické dáta)
  "GET /api/archive/officials": (params) =>
    filterAndPaginate(
      officials.filter((o) => o.state?.name === "INACTIVE"),
      params,
    ),
  "GET /api/archive/statements": (params) =>
    filterAndPaginate(
      statements.filter((s) => s.year < 2023),
      params,
    ),
};

// Hlavná funkcia pre mock fetch
export const mockFetch = async (url, options = {}) => {
  const method = options.method || "GET";
  const params = parseQueryParams(url);
  const path = url.split("?")[0];

  // Simulácia oneskorenia
  if (mockConfig.mockDelay > 0) {
    await delay(mockConfig.mockDelay);
  }

  // Logovanie
  if (mockConfig.logMockCalls) {
    console.log(`[MOCK] ${method} ${path}`, params);
  }

  // Hľadanie handlera
  let response = null;
  let matched = false;

  // Presná zhoda
  const exactKey = `${method} ${path}`;
  if (routeHandlers[exactKey]) {
    response = routeHandlers[exactKey](params);
    matched = true;
  }

  // Zhoda s parametrom :id
  if (!matched) {
    for (const [pattern, handler] of Object.entries(routeHandlers)) {
      const [routeMethod, routePath] = pattern.split(" ");
      if (routeMethod !== method) continue;

      // Kontrola parametrov v ceste
      if (routePath.includes("/:")) {
        const pathParts = routePath.split("/");
        const urlParts = path.split("/");

        if (pathParts.length === urlParts.length) {
          let isMatch = true;
          let extractedParam = null;

          for (let i = 0; i < pathParts.length; i++) {
            if (pathParts[i].startsWith(":")) {
              extractedParam = urlParts[i];
            } else if (pathParts[i] !== urlParts[i]) {
              isMatch = false;
              break;
            }
          }

          if (isMatch && extractedParam) {
            response = handler(extractedParam);
            matched = true;
            break;
          }
        }
      }
    }
  }

  // POST/PUT/DELETE operácie
  if (
    !matched &&
    (method === "POST" || method === "PUT" || method === "DELETE")
  ) {
    const body = options.body ? JSON.parse(options.body) : {};

    if (method === "POST") {
      response = {
        ...body,
        id: `new-${Date.now()}`,
        created: new Date().toISOString(),
      };
    } else if (method === "PUT") {
      response = { ...body, updated: new Date().toISOString() };
    } else if (method === "DELETE") {
      response = { success: true };
    }
    matched = true;
  }

  if (!matched) {
    console.warn(`[MOCK] No handler for ${method} ${path}`);
    return {
      ok: false,
      status: 404,
      json: async () => ({ error: "Not found", path }),
    };
  }

  return {
    ok: true,
    status: 200,
    json: async () => response,
    text: async () => JSON.stringify(response),
  };
};

export default mockFetch;
